import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';

export default function ChangeLogSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [changes, setChanges] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: ''
  });

  useEffect(() => {
    loadChanges();
  }, [eventId]);

  const loadChanges = async () => {
    const { data } = await supabase
      .from('change_log')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });
    if (data) setChanges(data);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await supabase
        .from('change_log')
        .update(formData)
        .eq('id', editingId);
    } else {
      await supabase
        .from('change_log')
        .insert({ ...formData, event_id: eventId });
    }
    resetForm();
    loadChanges();
  };

  const deleteEntry = async (id: string) => {
    await supabase.from('change_log').delete().eq('id', id);
    setDeleteId(null);
    loadChanges();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      author: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  const editEntry = (entry: any) => {
    setFormData({
      title: entry.title,
      description: entry.description,
      author: entry.author
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Change Log</h2>
          <p className="text-zinc-500 mt-1">Track updates and modifications</p>
        </div>
        {permissions.canEdit && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-white text-zinc-900 hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Add Change
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit' : 'Add'} Change
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-zinc-300">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Updated schedule for Day 2"
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-zinc-300">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder="What changed and why..."
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="author" className="text-zinc-300">Author</Label>
              <Input
                id="author"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="Your name"
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" className="bg-white text-zinc-900 hover:bg-zinc-200">
                {editingId ? 'Update' : 'Add'} Change
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-zinc-400 hover:text-white">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      ) : changes.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-8 text-center">
          <p className="text-zinc-500">No changes logged yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {changes.map((change, index) => (
            <div key={change.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-5 hover:border-zinc-700/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 text-sm font-medium">
                    {changes.length - index}
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-white mb-1">{change.title}</h3>
                      {change.description && (
                        <p className="text-zinc-400 text-sm whitespace-pre-wrap mb-3">{change.description}</p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-zinc-500">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {change.created_at && format(new Date(change.created_at), 'MMM d, yyyy h:mm a')}
                        </div>
                        {change.author && (
                          <span>by {change.author}</span>
                        )}
                      </div>
                    </div>

                    {permissions.canEdit && (
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => editEntry(change)}
                          className="text-zinc-400 hover:text-white h-8"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(change.id)}
                          className="text-zinc-400 hover:text-red-400 h-8 w-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Change Log Entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this change log entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEntry(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
